/**
 * The Map Review URL's grammar: the workbench state a `/_/review/map` query
 * names, and the query that names a state.
 *
 * The URL is the third place the workbench state lives, beside the store and
 * browser storage: `state=1` marks it authoritative, so a link reproduces the
 * view the sender was looking at — the same clauses, the same ledger, the same
 * record. `@/lib/mapReviewState` owns what a spec is, shared with the storage
 * tier; this module owns only how a state is spelled in a query, and therefore
 * imports no Vue, no router and no storage. The page decodes `route.query` into
 * the shape below and hands it to the store; `router.replace` and
 * percent-encoding stay the page's business, which is why every value here is
 * returned raw.
 *
 * Two rules shape the spelling. A written query carries the marker plus only
 * the fields that differ from the defaults, so a clean view is `?state=1` and a
 * shared link is as short as the view is simple. And it emits fields in one
 * fixed order — the grammar's, stated by `OPTIONAL_FIELDS` — because the page
 * skips a write when the encoding has not changed: a stable spelling makes that
 * comparison meaningful, and makes two links diff like the two views they name.
 *
 * The query is a snapshot, never a step in a history: the page replaces the
 * address bar on every settled change, so a link is the whole picture and
 * browser Back still leaves the page. And `spec` travels as the store's own
 * `MapReviewSpec`, not a compact alias — one shape means the validator shared
 * with the storage tier is the only one, and a debugger reading a link sees the
 * same keys the code and the server use.
 */

import {
	defaultLayerVisibility,
	type LayerVisibility,
} from "@/lib/mapReviewLayers";
import {
	MAP_REVIEW_TABLES,
	emptyMapReviewSpec,
	parseSpec,
	type MapReviewHydratedState,
} from "@/lib/mapReviewState";
import type { MapReviewSort, MapReviewSpec, MapReviewTable } from "@/type/api";

/**
 * The value of the `state` marker, and the grammar version it asserts. A URL
 * written by an older codec may name a field whose meaning this one changed, so
 * the marker is checked for equality rather than for presence: a link from
 * another version reads as a bare URL, which restores the visitor's own
 * storage, instead of half-applying a grammar it was not written in.
 */
export const MAP_REVIEW_URL_VERSION = 1;

/** The table a bare state opens: the workbench's home ledger. */
export const MAP_REVIEW_URL_DEFAULT_TABLE: MapReviewTable = "treatment";

/** The page size a bare state uses. It is the store's own value; the URL states
 * a limit only when one is opened with a size the page would not otherwise
 * choose, so the grammar does not depend on there being a control for it. */
export const MAP_REVIEW_URL_DEFAULT_LIMIT = 250;

/** The left-rail panes a state can name; null is the rail closed. */
export type MapReviewUrlPanel = "details" | "analysis" | "ask";

/** A selected record's table, or the one record that is not a ledger: a
 * contact, which the details rail resolves on its own. */
export type MapReviewUrlKind = MapReviewTable | "contact";

/** A selected record. Its id is stated apart from the table because a record
 * opened from a link may not be on the page the link also names — the details
 * rail reads it by id rather than from the ledger. */
export interface MapReviewUrlItem {
	kind: MapReviewUrlKind;
	id: number;
}

/** The map's camera. The values are the map's own units; `encodeCamera` rounds
 * them on the way into the URL. */
export interface MapReviewUrlCamera {
	lng: number;
	lat: number;
	zoom: number;
}

/** The whole state a URL can name: the subset the store hydrates in one batch,
 * plus the three pieces the view alone owns (the selected record, the open rail
 * and the camera) and the layer checkboxes. */
export interface MapReviewUrlState extends MapReviewHydratedState {
	item: MapReviewUrlItem | null;
	panel: MapReviewUrlPanel | null;
	camera: MapReviewUrlCamera | null;
	layers: LayerVisibility;
}

/** `present:false` means the query names no usable state — no marker, a marker
 * from another version, or a malformed member. The page then behaves as a bare
 * URL, which restores the visitor's own storage exactly as before links
 * existed. */
export type DecodedMapReviewUrl =
	| { present: true; state: MapReviewUrlState }
	| { present: false; state: null };

/** The params beside the marker, in the order a written query emits them. Each
 * is optional and takes its default when absent; a value that is not a string
 * is malformed rather than absent. */
const OPTIONAL_FIELDS = [
	"ledger",
	"table",
	"item",
	"page",
	"limit",
	"sort",
	"panel",
	"map",
	"layers",
	"spec",
] as const;

type OptionalField = (typeof OPTIONAL_FIELDS)[number];

/** The state a bare `?state=1` names: the defaults every absent field falls
 * back to. */
export function defaultMapReviewUrlState(): MapReviewUrlState {
	return {
		spec: emptyMapReviewSpec(),
		activeTable: MAP_REVIEW_URL_DEFAULT_TABLE,
		page: 0,
		limit: MAP_REVIEW_URL_DEFAULT_LIMIT,
		sort: null,
		drawerOpen: false,
		item: null,
		panel: null,
		camera: null,
		layers: defaultLayerVisibility(),
	};
}

/**
 * The canonical query for a state: the marker, plus only the fields that differ
 * from `defaultMapReviewUrlState`, in the grammar's order.
 *
 * Values are raw. The router percent-encodes them on write and decodes them on
 * read, so escaping here would double-encode `spec` and make a debugger show a
 * query instead of the spec the code and the server use.
 */
export function encodeMapReviewUrl(
	state: MapReviewUrlState,
): Record<string, string> {
	const query: Record<string, string> = {
		state: String(MAP_REVIEW_URL_VERSION),
	};
	const ledger = encodeLedger(state);
	if (ledger !== null) query.ledger = ledger;
	if (state.activeTable !== MAP_REVIEW_URL_DEFAULT_TABLE) {
		query.table = state.activeTable;
	}
	if (state.item !== null) query.item = encodeItem(state.item);
	if (state.page !== 0) query.page = String(state.page);
	if (state.limit !== MAP_REVIEW_URL_DEFAULT_LIMIT) {
		query.limit = String(state.limit);
	}
	if (state.sort !== null) query.sort = encodeSort(state.sort);
	if (state.panel !== null) query.panel = state.panel;
	if (state.camera !== null) query.map = encodeCamera(state.camera);
	const layers = encodeLayers(state.layers);
	if (layers !== null) query.layers = layers;
	// The spec travels in the store's own shape, but never carries its dead
	// search boxes: `spec.search` is not part of this grammar (the per-table
	// search boxes are gone and nothing resolves them), so a value there is not a
	// narrowing the URL should preserve.
	if (specNarrows(state.spec)) {
		query.spec = JSON.stringify({
			...state.spec,
			search: emptyMapReviewSpec().search,
		});
	}
	return query;
}

/**
 * The state a query names. Whole-state rejection: a present marker with any
 * invalid member yields `present:false`, so a partially-applied link is never a
 * view nobody asked for. Dropping one clause instead would need a per-field
 * policy with no better answer than "discard", and a hand-edited URL would
 * otherwise reach the server as a 422.
 *
 * An absent (or null) param takes its default. A param that is not a string —
 * a repeated key arrives as an array — is malformed rather than absent: the
 * grammar has one spelling per field, and this page writes only that one.
 */
export function decodeMapReviewUrl(
	query: Record<string, unknown>,
): DecodedMapReviewUrl {
	const state = decodeState(query);
	return state === null
		? { present: false, state: null }
		: { present: true, state };
}

/** The decoded state, or null when anything in the query is unusable. */
function decodeState(query: Record<string, unknown>): MapReviewUrlState | null {
	if (query.state !== String(MAP_REVIEW_URL_VERSION)) return null;
	const stated: Partial<Record<OptionalField, string>> = {};
	for (const field of OPTIONAL_FIELDS) {
		const value = query[field];
		if (value === undefined || value === null) continue;
		if (typeof value !== "string") return null;
		stated[field] = value;
	}

	const state = defaultMapReviewUrlState();
	if (stated.ledger !== undefined) {
		const ledger = decodeLedger(stated.ledger);
		if (ledger === null) return null;
		state.drawerOpen = ledger.drawerOpen;
	}
	if (stated.table !== undefined) {
		if (!isWorkbenchTable(stated.table)) return null;
		state.activeTable = stated.table;
	}
	if (stated.item !== undefined) {
		const item = decodeItem(stated.item);
		if (item === null) return null;
		state.item = item;
	}
	if (stated.page !== undefined) {
		const page = parseInteger(stated.page, 0);
		if (page === null) return null;
		state.page = page;
	}
	if (stated.limit !== undefined) {
		const limit = parseInteger(stated.limit, 1);
		if (limit === null) return null;
		state.limit = limit;
	}
	if (stated.sort !== undefined) {
		const sort = decodeSort(stated.sort);
		if (sort === null) return null;
		state.sort = sort;
	}
	if (stated.panel !== undefined) {
		if (
			stated.panel !== "details" &&
			stated.panel !== "analysis" &&
			stated.panel !== "ask"
		) {
			return null;
		}
		state.panel = stated.panel;
	}
	if (stated.map !== undefined) {
		const camera = decodeCamera(stated.map);
		if (camera === null) return null;
		state.camera = camera;
	}
	if (stated.layers !== undefined) {
		const layers = decodeLayers(stated.layers);
		if (layers === null) return null;
		state.layers = layers;
	}
	if (stated.spec !== undefined) {
		let parsed: unknown;
		try {
			parsed = JSON.parse(stated.spec);
		} catch {
			return null;
		}
		const spec = parseSpec(parsed);
		if (spec === null) return null;
		// The dead search boxes are not carried: a link that names one would
		// otherwise show an applied-filter chip for a narrowing the ledger no
		// longer performs, exactly as a restored visit's search is cleared.
		spec.search = emptyMapReviewSpec().search;
		state.spec = spec;
	}
	return state;
}

/** The ledger marker, or null when the drawer is closed. There is one ledger
 * surface now: the drawer, which expands to the top rather than handing over to
 * a second full-screen table. */
export function encodeLedger(state: MapReviewUrlState): string | null {
	return state.drawerOpen ? "drawer" : null;
}

/** The drawer flag a `ledger` value names, or null for an unknown word. `full`
 * is accepted for older links and opens the drawer: the full-screen table it
 * named was folded into the drawer's expanded state. */
export function decodeLedger(value: string): { drawerOpen: boolean } | null {
	if (value === "full" || value === "drawer") return { drawerOpen: true };
	return null;
}

/** `<kind>:<id>`. */
export function encodeItem(item: MapReviewUrlItem): string {
	return `${item.kind}:${item.id}`;
}

/** The record an `item` value names, or null. Split at the FIRST colon: a
 * ledger kind and `contact` contain none, so the id is the rest of the
 * value. */
export function decodeItem(value: string): MapReviewUrlItem | null {
	const separator = value.indexOf(":");
	if (separator < 0) return null;
	const id = parseInteger(value.slice(separator + 1), 1);
	if (id === null) return null;
	const kind = value.slice(0, separator);
	if (kind === "contact") return { kind, id };
	if (!isWorkbenchTable(kind)) return null;
	return { kind, id };
}

/** `<column>:<asc|desc>`. */
export function encodeSort(sort: MapReviewSort): string {
	return `${sort.column}:${sort.desc ? "desc" : "asc"}`;
}

/**
 * The sort a value names, or null. The column is not checked against the
 * column registry: that registry is the UI's, and the server is the one that
 * knows which columns it sorts — a non-empty column is this grammar's whole
 * requirement.
 */
export function decodeSort(value: string): MapReviewSort | null {
	const separator = value.indexOf(":");
	if (separator <= 0) return null;
	const direction = value.slice(separator + 1);
	if (direction !== "asc" && direction !== "desc") return null;
	return { column: value.slice(0, separator), desc: direction === "desc" };
}

/** `<lng>,<lat>,<zoom>`, rounded to 5 decimal places of position (~1 m) and 2
 * of zoom so a link carries a view rather than a dozen characters of noise.
 * `moveend` is the only writer trigger, so rounding cannot fight the map, and
 * rounding an already-rounded camera is a no-op — encoding stays idempotent. */
export function encodeCamera(camera: MapReviewUrlCamera): string {
	return `${camera.lng.toFixed(5)},${camera.lat.toFixed(5)},${camera.zoom.toFixed(2)}`;
}

/** The camera a value names, or null. The map's world bounds are the grammar's:
 * a value outside them is not a view this page can be showing. */
export function decodeCamera(value: string): MapReviewUrlCamera | null {
	const parts = value.split(",");
	if (parts.length !== 3) return null;
	const lng = parseFiniteNumber(parts[0]);
	const lat = parseFiniteNumber(parts[1]);
	const zoom = parseFiniteNumber(parts[2]);
	if (lng === null || lat === null || zoom === null) return null;
	if (lng < -180 || lng > 180) return null;
	if (lat < -90 || lat > 90) return null;
	if (zoom < 0 || zoom > 24) return null;
	return { lng, lat, zoom };
}

/** The checkboxes that differ from `defaultLayerVisibility`, as
 * `key:0|1` entries in the default's own key order, or null when every row is
 * as it starts. Hiding a layer is the common case, so the list names only what
 * the viewer changed and defaults to nothing. */
export function encodeLayers(visibility: LayerVisibility): string | null {
	const defaults = defaultLayerVisibility();
	const deviations: string[] = [];
	for (const key of Object.keys(defaults) as (keyof LayerVisibility)[]) {
		if (visibility[key] === defaults[key]) continue;
		deviations.push(`${key}:${visibility[key] ? 1 : 0}`);
	}
	return deviations.length === 0 ? null : deviations.join(",");
}

/** The visibility a `layers` value names, merged over the defaults, or null
 * when an entry names no row or states a value other than 0/1. Merging rather
 * than replacing means a link written before a layer existed still opens, with
 * the new row at its default. */
export function decodeLayers(value: string): LayerVisibility | null {
	const visibility = defaultLayerVisibility();
	for (const entry of value.split(",")) {
		const separator = entry.indexOf(":");
		if (separator <= 0) return null;
		const key = entry.slice(0, separator);
		const flag = entry.slice(separator + 1);
		if (flag !== "0" && flag !== "1") return null;
		if (!Object.prototype.hasOwnProperty.call(visibility, key)) return null;
		visibility[key as keyof LayerVisibility] = flag === "1";
	}
	return visibility;
}

/** Whether a value names a ledger tab THIS surface offers. `isMapReviewTable`
 * also accepts the lab's `pool`, which is a resolver table without a workbench
 * tab: a URL naming it would have no page to open, so the grammar does not
 * spell it. */
function isWorkbenchTable(value: string): value is MapReviewTable {
	return MAP_REVIEW_TABLES.some((table) => table === value);
}

/** Whether a spec narrows anything, i.e. whether a URL has to carry it. The
 * comparison is by value — the store replaces the spec object on every
 * mutation, so identity says nothing — and it mirrors `emptyMapReviewSpec`:
 * no clauses, no zone selection, no buffers, no Feature membership and the
 * default buffer mode. The search boxes are dead state (see the encoder). */
function specNarrows(spec: MapReviewSpec): boolean {
	if (spec.filters.length > 0) return true;
	if (spec.zones.length > 0) return true;
	if (spec.temp_zones.length > 0) return true;
	if (spec.feature_ids !== undefined && spec.feature_ids.length > 0)
		return true;
	if (spec.buffer_combine !== undefined && spec.buffer_combine !== "union") {
		return true;
	}
	// The search boxes are dead state, not a narrowing: a spec whose only content
	// is a search term is the empty spec as far as the URL is concerned.
	return false;
}

/** A canonical decimal integer at or above `minimum`, or null. Digits only: a
 * value this codec did not write is not assumed to mean what `Number` makes of
 * it (`Number("")` is 0, `Number("1e3")` is 1000). */
function parseInteger(text: string, minimum: number): number | null {
	if (!/^\d+$/.test(text)) return null;
	const value = Number(text);
	return Number.isSafeInteger(value) && value >= minimum ? value : null;
}

/** A finite number, or null. Blank text is rejected explicitly because
 * `Number("")` is 0. */
function parseFiniteNumber(text: string): number | null {
	const trimmed = text.trim();
	if (trimmed === "") return null;
	const value = Number(trimmed);
	return Number.isFinite(value) ? value : null;
}
